<script setup lang="ts">
defineProps<{
    moduleLabel: string;
}>();

const reloadPage = () => {
    window.location.reload();
};
</script>

<template>
    <div class="flex items-center justify-center min-h-[calc(100vh-12rem)] p-4">
        <div
            class="w-full max-w-3xl rounded-3xl bg-white dark:bg-slate-900 px-6 py-10 sm:px-12 sm:py-14 shadow-sm ring-1 ring-slate-100 dark:ring-white/10">
            <div class="flex flex-col items-center text-center">
                <!-- Animated cogs (lucide) -->
                <div class="relative h-32 mx-auto w-32 mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="lucide lucide-cog absolute top-2 left-2 h-20 w-20 text-indigo-500 anim-spin-slow"
                        aria-hidden="true">
                        <path d="M11 10.27 7 3.34"></path>
                        <path d="m11 13.73-4 6.93"></path>
                        <path d="M12 22v-2"></path>
                        <path d="M12 2v2"></path>
                        <path d="M14 12h8"></path>
                        <path d="m17 20.66-1-1.73"></path>
                        <path d="m17 3.34-1 1.73"></path>
                        <path d="M2 12h2"></path>
                        <path d="m20.66 17-1.73-1"></path>
                        <path d="m20.66 7-1.73 1"></path>
                        <path d="m3.34 17 1.73-1"></path>
                        <path d="m3.34 7 1.73 1"></path>
                        <circle cx="12" cy="12" r="2"></circle>
                        <circle cx="12" cy="12" r="8"></circle>
                    </svg>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="lucide lucide-cog absolute bottom-0 right-0 h-14 w-14 text-violet-400 anim-spin-reverse"
                        aria-hidden="true">
                        <path d="M11 10.27 7 3.34"></path>
                        <path d="m11 13.73-4 6.93"></path>
                        <path d="M12 22v-2"></path>
                        <path d="M12 2v2"></path>
                        <path d="M14 12h8"></path>
                        <path d="m17 20.66-1-1.73"></path>
                        <path d="m17 3.34-1 1.73"></path>
                        <path d="M2 12h2"></path>
                        <path d="m20.66 17-1.73-1"></path>
                        <path d="m20.66 7-1.73 1"></path>
                        <path d="m3.34 17 1.73-1"></path>
                        <path d="m3.34 7 1.73 1"></path>
                        <circle cx="12" cy="12" r="2"></circle>
                        <circle cx="12" cy="12" r="8"></circle>
                    </svg>
                </div>

                <h1 class="m-0 text-2xl sm:text-3xl font-semibold text-slate-900 dark:text-slate-50">
                    Scheduled Maintenance in Progress
                </h1>

                <!-- <p class="mt-3 max-w-xl text-slate-600 dark:text-slate-400">
                    We are currently performing system maintenance and platform enhancements to improve reliability,
                    performance, and data synchronization across device configuration and integration services.
                    During this time, access to <strong class="text-slate-900 dark:text-slate-200">{{ moduleLabel
                        }}</strong>
                    is temporarily unavailable.
                </p>

                <p class="mt-3 max-w-xl text-slate-600 dark:text-slate-400">
                    All previously saved data remains secure. Service will be restored shortly.
                    Thank you for your patience and understanding.
                </p> -->

                <!-- <div
                    class="mt-6 inline-flex items-center gap-2 rounded-full bg-slate-50 dark:bg-white/5 px-4 py-2 text-sm text-slate-700 dark:text-slate-300 ring-1 ring-slate-200/70 dark:ring-white/10">
                    <span class="cm-liveDot" aria-hidden="true"></span>
                    <span><strong class="font-semibold">Live</strong> · engineers actively working</span>
                </div> -->

                <div class="mt-7">
                    <Button label="Try Again" icon="pi pi-refresh"
                        class="bg-blue-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-500 transition duration-150"
                        @click="reloadPage" />
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.anim-spin-slow {
    animation: cm-rotate 6.5s linear infinite;
    will-change: transform;
}

.anim-spin-reverse {
    animation: cm-rotate-rev 5.2s linear infinite;
    will-change: transform;
}

.cm-liveDot {
    width: 10px;
    height: 10px;
    border-radius: 999px;
    background: rgb(16 185 129);
    /* emerald-500 */
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.45);
    animation: cm-pulse 1.6s ease-in-out infinite;
    will-change: transform, box-shadow;
}

@keyframes cm-rotate {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

@keyframes cm-rotate-rev {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(-360deg);
    }
}

@keyframes cm-pulse {
    0% {
        transform: scale(0.95);
        box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.35);
    }

    65% {
        transform: scale(1);
        box-shadow: 0 0 0 8px rgba(16, 185, 129, 0);
    }

    100% {
        transform: scale(0.95);
        box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
    }
}

@media (prefers-reduced-motion: reduce) {

    .anim-spin-slow,
    .anim-spin-reverse,
    .cm-liveDot {
        animation: none !important;
    }
}
</style>
